import { Company, ICompany } from '@app/models/Company'
import { EmailAddress, IEmailAddress } from '@app/models/EmailAddress'
import { IPhoneNumber, PhoneNumber } from '@app/models/PhoneNumber'
import { List, Record } from 'immutable'

export interface IQuickContact {
  readonly company?: ICompany
  readonly contactId?: string
  readonly emailAddresses: List<IEmailAddress>
  readonly error?: string
  readonly firstName: string
  readonly isCompany: boolean
  readonly isValid: boolean
  readonly lastName: string
  readonly phoneNumbers: List<IPhoneNumber>
}

const QuickContactRecord = Record({
  company: undefined,
  contactId: undefined,
  emailAddresses: List<EmailAddress>(),
  error: undefined,
  firstName: '',
  isCompany: false,
  isValid: false,
  lastName: '',
  phoneNumbers: List<PhoneNumber>(),
})

export class QuickContact extends QuickContactRecord implements IQuickContact {
  public static fromJS(initialState?: Partial<IQuickContact>) {
    if (!initialState) {
      return new QuickContact()
    }

    const { company, emailAddresses, phoneNumbers, ...rest } = initialState

    return new QuickContact({
      company: company ? Company.fromJS(company) : undefined,
      emailAddresses: emailAddresses
        ? List(emailAddresses.map((e) => EmailAddress.fromJS(e)))
        : List<EmailAddress>(),
      phoneNumbers: phoneNumbers
        ? List(phoneNumbers.map((p) => PhoneNumber.fromJS(p)))
        : List<PhoneNumber>(),
      ...rest,
    })
  }

  public readonly company?: Company
  public readonly contactId?: string
  public readonly emailAddresses: List<EmailAddress>
  public readonly error?: string
  public readonly firstName: string
  public readonly isCompany: boolean
  public readonly isValid: boolean
  public readonly lastName: string
  public readonly phoneNumbers: List<PhoneNumber>

  constructor(props?: Partial<QuickContact>) {
    props ? super(props) : super()
  }

  public validate(): QuickContact {
    const errors: string[] = []

    if (!this.lastName) {
      errors.push(
        `${this.isCompany ? 'Company name' : 'Last name'} is required`,
      )
    }

    if (!this.isCompany) {
      if (!this.firstName) {
        errors.push('First name is required')
      }

      const emailCount = this.emailAddresses
        .filter((e) => e !== undefined && e.emailAddress.length > 0)
        .count()
      const phoneCount = this.phoneNumbers
        .filter((p) => p !== undefined && p.phoneNumber.length >= 10)
        .count()

      if (emailCount === 0 && phoneCount === 0) {
        errors.push('At lease one phone number or email address is required')
      }
    }

    return this.set('error', errors.join('\n')).set(
      'isValid',
      errors.length === 0,
    ) as QuickContact
  }
}
